import React, { useEffect } from 'react'

import {
  Box,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaCompanyStepDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { CompanyLegalEntityFiller } from 'modules/payroll_integrations_a3innuva/components/SettingsSteps/CodeFillers/CompanyLegalEntityFiller'
import {
  companyCodesToOptions,
  handleA3innuvaNetworkError,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import { A3InnuvaWizardData } from 'modules/payroll_integrations_a3innuva/lib/types'

const CompanyStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()
  const {
    data: companyStepData,
    loading,
    error,
  } = useQuery(A3InnuvaCompanyStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      organizationalUnitId: data.answers.organizational_unit_id,
    },
  })

  useEffect(() => {
    if (error) {
      handleA3innuvaNetworkError(error)
    }
  }, [error])

  const legalEntities = companyStepData?.companies.legalEntitiesConnection.nodes || []
  const companyCodes = companyStepData?.payrollIntegrationsA3innuva.companyCodes || []
  const options = companyCodesToOptions(companyCodes)

  useOnNext(() => {
    const legalEntityKeys = Object.keys(data.answers).filter((key) =>
      /^legal_entity_\d+$/.test(key)
    )
    const legalEntityValues = legalEntityKeys.map((key) => data.answers[key])

    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsCompanySelected, {
      company_id: session.company.id,
      access_id: session.access.id,
      company: legalEntityValues,
    })
  })

  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.company.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.company.description'
          )}
        />
        <Box justifyContent="flexStart" flexDirection="column" gap="s32">
          <Box paddingBottom="s40">
            {
              <CompanyLegalEntityFiller
                data={data}
                onDataChanged={onDataChanged}
                completed
                loading={loading}
                options={options}
                legalEntities={legalEntities}
              />
            }
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const companyStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.company.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <CompanyStep {...props} />,
  isCompleted: (data) => handleIsCompleted(data),
})

const handleIsCompleted = (data: A3InnuvaWizardData) => {
  const companyCodeSelected = !!data.answers.company_code_enabled
  if (companyCodeSelected && data.answers.company) {
    return true
  }

  const legalEntityKeys = Object.keys(data.answers).filter((key) => /^legal_entity_\d+$/.test(key))

  const allLegalEntitiesFilled =
    legalEntityKeys.length > 0 && legalEntityKeys.some((key) => !!data.answers[key])

  return allLegalEntitiesFilled
}
